import { type RefObject, useEffect, useState } from "react";

function useVideoOnScreen(ref: RefObject<HTMLElement | null> | null) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const element = ref?.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const fullscreenElement = document.fullscreenElement;
        const isFullscreen =
          !!fullscreenElement &&
          (fullscreenElement === element ||
            element.contains(fullscreenElement));
        setIntersecting(isFullscreen || entry.isIntersecting);
      },
      { threshold: 0.01 },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return isIntersecting;
}

// Same logic as `useVideoOnScreen`, but keyed on the element itself rather
// than a ref object. Use this when the element comes from state/a prop —
// a ref object's identity never changes even when `ref.current` is
// reassigned to a different element, so `useVideoOnScreen` would attach its
// IntersectionObserver once and never re-attach as the element changes.
function useElementOnScreen(element: HTMLElement | null) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const fullscreenElement = document.fullscreenElement;
        const isFullscreen =
          !!fullscreenElement &&
          (fullscreenElement === element ||
            element.contains(fullscreenElement));
        setIntersecting(isFullscreen || entry.isIntersecting);
      },
      { threshold: 0.01 },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [element]);

  return isIntersecting;
}

export { useVideoOnScreen, useElementOnScreen };
